import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from 'react';

import cloneDeep from 'lodash/cloneDeep';

import Modal from '../../../../../utils/ModalComponet';
import {
  validateBookingOptionFields,
  validatePriceFormat,
} from '../../../../../../infrastructure/utils/common';
import {
  AdditionalCharges,
  BookingOption,
  BookingOptionSubType,
} from '../../../../../../types/Activity';
import BookingOptionContent from '../..';
import S from '../../styles';
import {
  ACTIVITY_AVAILABLE_DAY_WEEKS,
  DEFAULT_ADDITIONAL_CHARGES,
} from '../../../../../../infrastructure/constants/activities';
import { ActivityContext } from '../../../../../../contexts/Activity';
import { isAddonsSupported } from '../../../../../forms/EditPeekAndRedeamOrderForm/helpers';
import Error from '../../../../../../components/stateMessage/Error';

type CreateBookingOptionProps = {
  isOpen: boolean;
  closeModal: () => void;
  createBookingOption(option: Partial<BookingOption>): void;
};

type EditBookingOptionProps = {
  option: BookingOption;
  onCancel(): void;
  bookingOptionSubmitHandle(id: string, option: Partial<BookingOption>): void;
  onDelete(): Promise<void>;
  isLastBookingOption: boolean;
};

type Props = {
  isNotIntegrationEnabled?: boolean;
  isFareharborSource?: boolean;
} & Partial<EditBookingOptionProps & CreateBookingOptionProps>;

const defaultValues: Partial<BookingOption> = {
  attendeesRequired: false,
  attendeesMinimum: 1,
  hours: ACTIVITY_AVAILABLE_DAY_WEEKS,
  additionalCharges: DEFAULT_ADDITIONAL_CHARGES,
};

const getAdditionalCharges = (additionalCharges: AdditionalCharges[]) => {
  if (Array.isArray(additionalCharges) && additionalCharges.length) {
    return additionalCharges;
  }
  return DEFAULT_ADDITIONAL_CHARGES;
};

const getDefaultFields = (
  option?: BookingOption,
  isAddonsSupported?: boolean
) => {
  let fields = { ...defaultValues };
  if (option) {
    fields = {
      ...option,
      bookingNotes: option.bookingNotes || '',
      additionalCharges: getAdditionalCharges(option.additionalCharges),
      attendeesRequired: option.attendeesRequired || false,
      description: option.description || '',
      duration: option.duration || 0,
      hours: option.hours || '',
      interval: option.interval || 0,
    };
  }
  if (!isAddonsSupported) {
    fields.subType = BookingOptionSubType.bookingOption;
  }
  return fields;
};

const BookingOptionModal = ({
  isOpen = false,
  closeModal,
  createBookingOption,
  bookingOptionSubmitHandle,
  isNotIntegrationEnabled,
  option,
  onCancel,
  onDelete,
  isLastBookingOption,
  isFareharborSource,
}: Props) => {
  const activityContext = useContext(ActivityContext);
  const [fieldsValue, setFieldsValue] = useState<Partial<BookingOption>>(
    getDefaultFields(option, isAddonsSupported(activityContext.source))
  );

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const hideMessage = () => {
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 5000);
  };

  const [deleteModal, setDeleteModal] = useState(false);
  const [noMaximumAttendees, setNoMaximumAttendees] = useState(() => {
    if (option) {
      return fieldsValue.attendeesMaximum === null ? true : false;
    }
    return false;
  });

  const isPriceValid = useMemo(() => validatePriceFormat(fieldsValue.price), [
    fieldsValue.price,
  ]);

  const onSave = event => {
    event.preventDefault();

    if (fieldsValue.additionalCharges) {
      if (isFareharborSource) {
        const hasAdditionalCharges = fieldsValue.additionalCharges.some(
          item => item.percent > 0
        );
        if (hasAdditionalCharges) {
          setErrorMessage(
            'A FareHarbor experience cannot have Gratuity or Vendor Fees specified'
          );
          hideMessage();
          return;
        }
      }

      fieldsValue.additionalCharges = fieldsValue.additionalCharges.map(
        charge => ({ ...charge, percent: Number(charge.percent) })
      );
    }

    if (option) {
      bookingOptionSubmitHandle &&
        bookingOptionSubmitHandle(option.id, fieldsValue);
    } else createBookingOption && createBookingOption(fieldsValue);
  };

  const onChange = useCallback(
    (key: keyof BookingOption, value) => {
      setFieldsValue({
        ...fieldsValue,
        [key]: value,
      });
    },
    [fieldsValue]
  );

  const onChangeAdditionalCharges = useCallback(
    (name, value) => {
      const updateAdditionalCharges = cloneDeep(fieldsValue.additionalCharges);
      const idX = updateAdditionalCharges.findIndex(
        charge => charge.name === name
      );
      if (idX !== -1) {
        updateAdditionalCharges[idX].percent = value;
        onChange('additionalCharges', updateAdditionalCharges);
      }
    },
    [fieldsValue.additionalCharges, onChange]
  );

  const isSaveBtnDisabled = !validateBookingOptionFields(
    fieldsValue,
    noMaximumAttendees
  );

  const onClose = () => {
    setDeleteModal(false);
  };

  const onCloseModal = () => {
    closeModal && closeModal();
    setFieldsValue(defaultValues);
  };

  useEffect(() => {
    return () => {
      setFieldsValue(defaultValues);
    };
  }, []);

  if (option) {
    return (
      <Modal open closeModal={onCloseModal}>
        <BookingOptionContent
          option={fieldsValue}
          isPriceValid={isPriceValid}
          noMaximumAttendees={noMaximumAttendees}
          onChange={onChange}
          isNotIntegrationEnabled={isNotIntegrationEnabled}
          onChangeAdditionalCharges={onChangeAdditionalCharges}
          onFormSubmit={onSave}
          setNoMaximumAttendees={setNoMaximumAttendees}
          setFieldsValue={setFieldsValue}
        >
          <Modal
            open={deleteModal}
            closeModal={onClose}
            cancel={onClose}
            action={async () => {
              onClose();
              onDelete && (await onDelete());
            }}
            actionButtonText={isLastBookingOption ? 'Confirm' : 'Delete'}
          >
            <div>
              {isLastBookingOption
                ? "You are deleting the last booking option from the activity. Activity will be marked as 'non-bookable'. Do you want to proceed?"
                : 'Are you sure you want to delete this option?'}
            </div>
          </Modal>
          <S.Buttons>
            <button
              className='btn btn-danger delete-btn'
              onClick={() => setDeleteModal(true)}
              type='button'
            >
              Delete
            </button>
            <button
              className='btn btn-def cancel-btn'
              onClick={onCancel}
              type='button'
            >
              Cancel
            </button>
            <button
              className='btn btn-primary'
              type='submit'
              disabled={isSaveBtnDisabled}
            >
              Edit
            </button>
          </S.Buttons>
        </BookingOptionContent>
        {!!errorMessage && <Error text={errorMessage} />}
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      closeModal={onCloseModal}
      cancel={onCloseModal}
      action={onSave}
      actionButtonText='Create'
      disabled={isSaveBtnDisabled}
    >
      <BookingOptionContent
        isNotIntegrationEnabled={isNotIntegrationEnabled}
        option={fieldsValue}
        isPriceValid={isPriceValid}
        noMaximumAttendees={noMaximumAttendees}
        onChangeAdditionalCharges={onChangeAdditionalCharges}
        onChange={onChange}
        setFieldsValue={setFieldsValue}
        onFormSubmit={onSave}
        setNoMaximumAttendees={setNoMaximumAttendees}
      />
      {!!errorMessage && <Error text={errorMessage} />}
    </Modal>
  );
};

export default BookingOptionModal;
